// Flows automation catalog (part 1 of 3 — catalog, toggle, and audit log
// only; auto-execution is a separate follow-up). Lists every automatable
// flow, lets an admin toggle each between "Needs Human Approval" (default)
// and "Automate," and shows the full per-flow change history.
import { useEffect, useState, useCallback } from 'react'
import {
  Box, Stack, Typography, Chip, Button, TextField, Paper, CircularProgress,
  Alert, Switch, FormControlLabel, Collapse, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material'
import { fetchFlows, updateFlowAutomate, updateFlowDescription, updateFlowFlowchart, deleteFlow, fetchFlowLog } from '../../api.js'
import { flowToggleCopy, buildFlowAutomatePayload } from '../../flowToggleCopy.js'
import ExpandableTextField from '../../ExpandableTextField.jsx'
import { matchesFlowSearch } from '../../flowSearch.js'
import { KNOWN_CHANNELS } from '../../constants/channels.js'
import { KNOWN_CLIENTS } from '../../constants/clients.js'
import { TEAMS, teamLabel } from '@cxhub/shared'
import { KNOWN_COMPANIES, COMPANY_TAG_STYLE } from '../../constants/companies.js'

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }) + ' ET'
}

function FlowLogSection({ flowKey, company, channel, client, team, open }) {
  const [log, setLog] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open || log !== null) return
    fetchFlowLog(flowKey, company, channel, client, team).then(setLog).catch((e) => setError(e.message))
  }, [open, flowKey, company, channel, client, team, log])

  return (
    <Collapse in={open} unmountOnExit>
      <Box sx={{ mt: 1, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {log === null ? (
          <CircularProgress size={16} />
        ) : log.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No changes recorded yet.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {log.map((entry) => (
              <Typography key={entry.id} variant="caption" color="text.secondary" component="div">
                <b>{entry.automate ? 'Automate' : 'Needs Human Approval'}</b> — {formatDate(entry.changedAt)}
                {entry.changedBy ? ` · ${entry.changedBy}` : ''}
                {entry.note ? ` — "${entry.note}"` : ''}
              </Typography>
            ))}
          </Stack>
        )}
      </Box>
    </Collapse>
  )
}

// Confirms a flow's automate toggle before it takes effect — mirrors
// TemplatesPage.jsx's DeleteConfirmDialog pattern (Dialog/DialogTitle/
// DialogContent/DialogActions, disabled Cancel + spinner on the confirm
// button while the request is in flight) rather than window.confirm() or a
// new one-off style. saving/error are FlowRow's own state, passed down
// rather than duplicated here, since the confirm button triggers the exact
// same updateFlowAutomate() call FlowRow's Switch used to call directly.
function FlowToggleConfirmDialog({ open, flow, targetAutomate, saving, error, onConfirm, onCancel }) {
  const copy = flowToggleCopy(targetAutomate)
  // Required exact-name-match confirmation — a client-side safeguard only
  // (no server schema change) so an admin can't toggle live automation with
  // an accidental click; must match flow.flowKey exactly (case-sensitive, no
  // extra trimming beyond what's natural). Resets whenever the dialog closes
  // (and therefore starts fresh on the next open), same as FlowRow's own
  // `note` resets in toggle()/cancelToggle().
  const [typedName, setTypedName] = useState('')
  useEffect(() => {
    if (!open) setTypedName('')
  }, [open])

  return (
    <Dialog open={open} onClose={() => !saving && onCancel()} maxWidth="xs" fullWidth>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {copy.warnsRetroactiveSweep ? (
          <Typography>
            Matching AI suggestions for <b>{flow?.flowKey}</b> will execute and send automatically from now on, without a human clicking Approve. This will also <b>immediately auto-execute any already-pending suggestions</b> for this flow — real TRQ/eBay writes will happen right away, not just for new messages going forward.
          </Typography>
        ) : (
          <Typography>
            Switch <b>{flow?.flowKey}</b> back to requiring a human to review and approve each suggestion before anything executes or sends.
          </Typography>
        )}
        <TextField
          size="small"
          fullWidth
          label="Type the flow name to confirm"
          placeholder={flow?.flowKey}
          helperText={`Type "${flow?.flowKey}" exactly to enable the confirm button.`}
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          disabled={saving}
          sx={{ mt: 2 }}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          color={copy.confirmColor}
          onClick={onConfirm}
          disabled={saving || typedName !== flow?.flowKey}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : copy.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Confirms a real, honest deletion of the whole (flowKey, company, channel,
// client) row before it takes effect — deleting a flow is at least as
// consequential as toggling Automate, so this uses the SAME "type the flow
// name to confirm" friction FlowToggleConfirmDialog has above, not the
// simpler click-through DeleteConfirmDialog TemplatesPage.jsx uses for
// templates.
function FlowDeleteConfirmDialog({ open, flow, saving, error, onConfirm, onCancel }) {
  const [typedName, setTypedName] = useState('')
  useEffect(() => {
    if (!open) setTypedName('')
  }, [open])

  return (
    <Dialog open={open} onClose={() => !saving && onCancel()} maxWidth="xs" fullWidth>
      <DialogTitle>Delete flow</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Typography>
          Permanently delete <b>{flow?.flowKey}</b> for {flow?.company} · {flow?.channel} · {teamLabel(flow?.team ?? 'customer_service')} · {flow?.client}, including its change history. This cannot be undone.
        </Typography>
        <TextField
          size="small"
          fullWidth
          label="Type the flow name to confirm"
          placeholder={flow?.flowKey}
          helperText={`Type "${flow?.flowKey}" exactly to enable the delete button.`}
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          disabled={saving}
          sx={{ mt: 2 }}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={saving || typedName !== flow?.flowKey}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {saving ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function FlowRow({ flow, onUpdated, onDeleted }) {
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [logOpen, setLogOpen] = useState(false)
  const [editingDescription, setEditingDescription] = useState(false)
  const [descriptionDraft, setDescriptionDraft] = useState(flow.description ?? '')
  const [savingDescription, setSavingDescription] = useState(false)
  const [editingFlowchart, setEditingFlowchart] = useState(false)
  const [flowchartDraft, setFlowchartDraft] = useState(flow.flowchartMermaid ?? '')
  const [savingFlowchart, setSavingFlowchart] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  async function confirmDelete() {
    setDeleting(true); setDeleteError(null)
    try {
      await deleteFlow(flow.flowKey, flow.company, flow.channel, flow.client, flow.team)
      setDeleteConfirmOpen(false)
      onDeleted(flow)
    } catch (e) {
      setDeleteError(e.message)
    } finally {
      setDeleting(false)
    }
  }

  function cancelDelete() {
    if (deleting) return
    setDeleteConfirmOpen(false)
    setDeleteError(null)
  }

  async function toggle() {
    setSaving(true); setError(null)
    try {
      const updated = await updateFlowAutomate(flow.flowKey, buildFlowAutomatePayload(flow, note))
      onUpdated(updated)
      setNote('')
      setConfirmOpen(false)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  function cancelToggle() {
    if (saving) return
    setConfirmOpen(false)
    setError(null)
  }

  function startEditingDescription() {
    setDescriptionDraft(flow.description ?? '')
    setEditingDescription(true)
  }

  async function saveDescription() {
    setSavingDescription(true); setError(null)
    try {
      const updated = await updateFlowDescription(flow.flowKey, flow.company, flow.channel, flow.client, descriptionDraft.trim(), flow.team)
      onUpdated(updated)
      setEditingDescription(false)
    } catch (e) {
      setError(e.message)
    } finally {
      setSavingDescription(false)
    }
  }

  function startEditingFlowchart() {
    setFlowchartDraft(flow.flowchartMermaid ?? '')
    setEditingFlowchart(true)
  }

  async function saveFlowchart() {
    setSavingFlowchart(true); setError(null)
    try {
      const updated = await updateFlowFlowchart(flow.flowKey, flow.company, flow.channel, flow.client, flowchartDraft.trim(), flow.team)
      onUpdated(updated)
      setEditingFlowchart(false)
    } catch (e) {
      setError(e.message)
    } finally {
      setSavingFlowchart(false)
    }
  }

  const flowchartViewerUrl = `/flows/${encodeURIComponent(flow.flowKey)}/flowchart?company=${encodeURIComponent(flow.company)}&channel=${encodeURIComponent(flow.channel)}&client=${encodeURIComponent(flow.client)}&team=${encodeURIComponent(flow.team ?? 'customer_service')}`

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: 13 }}>{flow.flowKey}</Typography>
          <Chip
            size="small"
            variant="filled"
            label={`${flow.company} · ${flow.channel} · ${flow.client}`}
            sx={{ ...(COMPANY_TAG_STYLE[flow.company] ?? {}), fontFamily: 'monospace', fontWeight: 700 }}
          />
          {/* Team (2026-10-07): part of the row's key, beside client. */}
          <Chip size="small" variant="outlined" data-testid="flow-team" label={teamLabel(flow.team ?? 'customer_service')} />
          <Chip size="small" label={flow.automate ? 'Automate' : 'Needs Human Approval'} color={flow.automate ? 'success' : 'default'} variant="outlined" />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {flow.updatedBy ? `Last changed by ${flow.updatedBy} — ` : ''}{formatDate(flow.updatedAt)}
          </Typography>
        </Stack>

        {editingDescription ? (
          <Stack spacing={1}>
            <ExpandableTextField
              size="small"
              multiline
              minRows={2}
              fullWidth
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              disabled={savingDescription}
              autoFocus
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small" variant="contained"
                onClick={saveDescription}
                disabled={savingDescription || !descriptionDraft.trim()}
              >
                {savingDescription ? 'Saving…' : 'Save'}
              </Button>
              <Button size="small" onClick={() => setEditingDescription(false)} disabled={savingDescription}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', flex: 1 }}>
              {flow.description ?? <em>No description yet.</em>}
            </Typography>
            <Button size="small" onClick={startEditingDescription} sx={{ flexShrink: 0 }}>
              Edit
            </Button>
          </Stack>
        )}

        {editingFlowchart ? (
          <Stack spacing={1}>
            <ExpandableTextField
              size="small"
              multiline
              minRows={4}
              fullWidth
              label="Mermaid flowchart source"
              value={flowchartDraft}
              onChange={(e) => setFlowchartDraft(e.target.value)}
              disabled={savingFlowchart}
              sx={{ fontFamily: 'monospace' }}
              autoFocus
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small" variant="contained"
                onClick={saveFlowchart}
                disabled={savingFlowchart || !flowchartDraft.trim()}
              >
                {savingFlowchart ? 'Saving…' : 'Save'}
              </Button>
              <Button size="small" onClick={() => setEditingFlowchart(false)} disabled={savingFlowchart}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            {flow.flowchartMermaid ? (
              <Button
                size="small" variant="outlined"
                component="a" href={flowchartViewerUrl} target="_blank" rel="noopener"
              >
                View Flowchart ↗
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                <em>No flowchart yet.</em>
              </Typography>
            )}
            <Button size="small" onClick={startEditingFlowchart} sx={{ flexShrink: 0 }}>
              {flow.flowchartMermaid ? 'Edit Flowchart' : 'Add Flowchart'}
            </Button>
          </Stack>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={<Switch size="small" checked={flow.automate} disabled={saving} onChange={() => setConfirmOpen(true)} />}
            label={<Typography variant="body2">{flow.automate ? 'Automate' : 'Needs Human Approval'}</Typography>}
          />
          <TextField
            size="small"
            placeholder="Optional note for this change..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={saving}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Button size="small" onClick={() => setLogOpen((v) => !v)}>
            {logOpen ? 'Hide history' : 'Show history'}
          </Button>
          <Button size="small" color="error" onClick={() => setDeleteConfirmOpen(true)}>
            Delete
          </Button>
        </Stack>

        {error && !confirmOpen && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        <FlowLogSection flowKey={flow.flowKey} company={flow.company} channel={flow.channel} client={flow.client} team={flow.team} open={logOpen} />
      </Stack>

      <FlowToggleConfirmDialog
        open={confirmOpen}
        flow={flow}
        targetAutomate={!flow.automate}
        saving={saving}
        error={error}
        onConfirm={toggle}
        onCancel={cancelToggle}
      />
      <FlowDeleteConfirmDialog
        open={deleteConfirmOpen}
        flow={flow}
        saving={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </Paper>
  )
}

export default function FlowsPage() {
  const [flows, setFlows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filterCompany, setFilterCompany] = useState('')
  const [filterChannel, setFilterChannel] = useState('')
  const [filterClient, setFilterClient] = useState('')
  const [filterTeam, setFilterTeam] = useState('')
  const [filterAutomate, setFilterAutomate] = useState('') // '', 'automate', 'manual'
  const [search, setSearch] = useState('')

  const load = useCallback(() => {
    setLoading(true); setError(null)
    fetchFlows()
      .then(setFlows)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  // A given flowKey can now appear multiple times (once per company/channel/
  // client combo) — match on the full (flowKey, company, channel, client)
  // quadruple, not flowKey alone, or updating one account's row would clobber
  // every other account's row sharing the same flowKey.
  function handleUpdated(updated) {
    if (!updated) return
    setFlows((prev) => prev.map((f) =>
      f.flowKey === updated.flowKey && f.company === updated.company && f.channel === updated.channel && f.client === updated.client && f.team === updated.team ? updated : f
    ))
  }

  function handleDeleted(deleted) {
    setFlows((prev) => prev.filter((f) =>
      !(f.flowKey === deleted.flowKey && f.company === deleted.company && f.channel === deleted.channel && f.client === deleted.client && f.team === deleted.team)
    ))
  }

  const filtered = flows.filter((f) =>
    (!filterCompany || f.company === filterCompany) &&
    (!filterChannel || f.channel === filterChannel) &&
    (!filterClient || f.client === filterClient) &&
    (!filterTeam || (f.team ?? 'customer_service') === filterTeam) &&
    (!filterAutomate || (filterAutomate === 'automate' ? f.automate : !f.automate)) &&
    matchesFlowSearch(f, search)
  )
  const hasFilters = filterCompany || filterChannel || filterClient || filterTeam || filterAutomate || search

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Flows</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Toggle a flow to "Automate" to let matching AI suggestions execute and send automatically, without a human clicking Approve. Every change is recorded below each flow.
      </Typography>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search flows by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Company</InputLabel>
          <Select value={filterCompany} label="Company" onChange={(e) => setFilterCompany(e.target.value)}>
            <MenuItem value="">All companies</MenuItem>
            {KNOWN_COMPANIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Channel</InputLabel>
          <Select value={filterChannel} label="Channel" onChange={(e) => setFilterChannel(e.target.value)}>
            <MenuItem value="">All channels</MenuItem>
            {KNOWN_CHANNELS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Client</InputLabel>
          <Select value={filterClient} label="Client" onChange={(e) => setFilterClient(e.target.value)}>
            <MenuItem value="">All clients</MenuItem>
            {KNOWN_CLIENTS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Team</InputLabel>
          <Select value={filterTeam} label="Team" onChange={(e) => setFilterTeam(e.target.value)} SelectDisplayProps={{ 'data-testid': 'flows-filter-team' }}>
            <MenuItem value="">All teams</MenuItem>
            {TEAMS.map((t) => <MenuItem key={t} value={t}>{teamLabel(t)}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterAutomate} label="Status" onChange={(e) => setFilterAutomate(e.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="automate">Automate</MenuItem>
            <MenuItem value="manual">Needs Human Approval</MenuItem>
          </Select>
        </FormControl>
        {hasFilters && (
          <Button size="small" variant="text" onClick={() => { setFilterCompany(''); setFilterChannel(''); setFilterClient(''); setFilterTeam(''); setFilterAutomate(''); setSearch('') }}>
            Clear
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : flows.length === 0 ? (
        <Typography color="text.secondary">No flows found — has the catalog been seeded yet?</Typography>
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary">No flows match the current filters.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map((flow) => (
            <FlowRow key={`${flow.flowKey}|${flow.company}|${flow.channel}|${flow.team}|${flow.client}`} flow={flow} onUpdated={handleUpdated} onDeleted={handleDeleted} />
          ))}
        </Stack>
      )}
    </Box>
  )
}
